import { Link } from "react-router-dom";
import type { ProductCard, SolutionCard } from "@itaas/pep-contracts";
import { ProductLogo } from "../../components/catalog/ProductLogo";
import { ProductPromotionBadge } from "../../components/commercial/ProductPromotionBadge";
import {
  labelCategory,
  labelIndustry,
  labelStatus,
  productDescription,
  productDisplayName,
  productTagline,
  solutionDescription,
  solutionDisplayName,
  statusBadgeClass,
} from "../../utils/labels";

export type CatalogDetailTarget =
  | { kind: "product"; product: ProductCard }
  | { kind: "solution"; solution: SolutionCard; products: ProductCard[] };

interface TrialCatalogDetailModalProps {
  target: CatalogDetailTarget | null;
  selected: boolean;
  onClose: () => void;
  onToggleSelect: () => void;
}

function BulletList({ items, title }: { items: string[]; title: string }) {
  if (!items.length) return null;
  return (
    <section className="mb-4">
      <h3 className="h6 fw-bold mb-2">{title}</h3>
      <ul className="list-unstyled pep-trial-detail-list mb-0">
        {items.map((item) => (
          <li key={item}>
            <span className="pep-trial-detail-list__mark" aria-hidden>
              ✓
            </span>
            {item}
          </li>
        ))}
      </ul>
    </section>
  );
}

function ValueGrid({ items, title }: { items: string[]; title: string }) {
  if (!items.length) return null;
  return (
    <section className="mb-4">
      <h3 className="h6 fw-bold mb-2">{title}</h3>
      <div className="row g-2">
        {items.map((item) => (
          <div className="col-sm-6" key={item}>
            <div className="pep-trial-detail-value p-2 rounded-3 h-100">
              <span className="text-primary fw-bold me-1">→</span>
              {item}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function ProductDetailBody({ product }: { product: ProductCard }) {
  const name = productDisplayName(product);
  const desc = productDescription(product);
  const tagline = productTagline(product);

  return (
    <>
      <div className="pep-trial-detail-hero mb-4">
        <ProductLogo slug={product.slug} name={name} size={72} className="pep-trial-detail-hero__logo" />
        <div className="flex-grow-1 min-w-0">
          <div className="d-flex flex-wrap gap-2 mb-2">
            <span className="badge text-bg-secondary text-uppercase" style={{ fontSize: "0.65rem" }}>
              {labelCategory(product.category)}
            </span>
            <span className={`badge ${statusBadgeClass(product.status)}`}>
              {labelStatus(product.status)}
            </span>
            {product.trialDays && (
              <span className="badge text-bg-light border">Essai {product.trialDays} j</span>
            )}
            <ProductPromotionBadge promotion={product.promotion} locale="fr" />
          </div>
          <h2 className="h4 fw-bold mb-1">{name}</h2>
          <p className="text-secondary mb-0 small">{tagline}</p>
        </div>
      </div>

      <section className="mb-4">
        <h3 className="h6 fw-bold mb-2">Présentation</h3>
        <p className="text-secondary mb-0">{desc}</p>
      </section>

      {product.availabilityNote && (
        <div className="alert alert-info border-0 small py-2 mb-4">{product.availabilityNote}</div>
      )}

      <ValueGrid items={product.valuePropositions ?? []} title={`Pourquoi ${name} ?`} />
      <BulletList items={product.features} title="Fonctionnalités clés" />
      <BulletList items={product.useCases ?? []} title="Cas d'usage" />

      {product.industries.length > 0 && (
        <section className="mb-0">
          <h3 className="h6 fw-bold mb-2">Secteurs</h3>
          <div className="d-flex flex-wrap gap-1">
            {product.industries.map((ind) => (
              <span key={ind} className="badge text-bg-light border">
                {labelIndustry(ind)}
              </span>
            ))}
          </div>
        </section>
      )}
    </>
  );
}

function SolutionDetailBody({
  solution,
  products,
}: {
  solution: SolutionCard;
  products: ProductCard[];
}) {
  const name = solutionDisplayName(solution);
  const desc = solutionDescription(solution);
  const heroSlug = products[0]?.slug ?? solution.products[0];

  return (
    <>
      <div className="pep-trial-detail-hero mb-4">
        {heroSlug ? (
          <ProductLogo slug={heroSlug} name={name} size={72} className="pep-trial-detail-hero__logo" />
        ) : (
          <div className="pep-product-logo-fallback pep-trial-detail-hero__logo" style={{ width: 72, height: 72 }}>
            {name.slice(0, 2).toUpperCase()}
          </div>
        )}
        <div className="flex-grow-1 min-w-0">
          <span className="badge text-bg-primary mb-2">Solution métier</span>
          <h2 className="h4 fw-bold mb-1">{name}</h2>
          <p className="text-secondary mb-0 small">
            {solution.descriptionFr ?? solution.description}
          </p>
        </div>
      </div>

      <section className="mb-4">
        <h3 className="h6 fw-bold mb-2">Présentation</h3>
        <p className="text-secondary mb-0">{desc}</p>
      </section>

      <BulletList items={solution.benefits ?? []} title="Bénéfices clés" />

      <section className="mb-4">
        <h3 className="h6 fw-bold mb-2">Modules inclus ({products.length})</h3>
        <div className="row g-2">
          {products.map((p) => (
            <div className="col-sm-6" key={p.slug}>
              <div className="pep-trial-detail-module d-flex align-items-center gap-2 p-2 rounded-3">
                <ProductLogo slug={p.slug} name={productDisplayName(p)} size={32} />
                <div className="min-w-0">
                  <div className="fw-semibold small text-truncate">{productDisplayName(p)}</div>
                  <div className="text-secondary text-truncate" style={{ fontSize: "0.7rem" }}>
                    {productTagline(p).slice(0, 60)}
                    {productTagline(p).length > 60 ? "…" : ""}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {solution.industries.length > 0 && (
        <section className="mb-0">
          <h3 className="h6 fw-bold mb-2">Secteurs</h3>
          <div className="d-flex flex-wrap gap-1">
            {solution.industries.map((ind) => (
              <span key={ind} className="badge text-bg-light border">
                {labelIndustry(ind)}
              </span>
            ))}
          </div>
        </section>
      )}
    </>
  );
}

export function TrialCatalogDetailModal({
  target,
  selected,
  onClose,
  onToggleSelect,
}: TrialCatalogDetailModalProps) {
  if (!target) return null;

  const fullPageUrl =
    target.kind === "product"
      ? `/products/${target.product.slug}`
      : `/solutions/${target.solution.slug}`;

  const title =
    target.kind === "product"
      ? productDisplayName(target.product)
      : solutionDisplayName(target.solution);

  return (
    <>
      <div
        className="modal show d-block pep-trial-detail-modal-wrap"
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pep-trial-detail-title"
        onKeyDown={(e) => e.key === "Escape" && onClose()}
      >
        <div
          className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-content pep-trial-detail-modal border-0 shadow">
            <div className="modal-header border-0 pb-0">
              <span id="pep-trial-detail-title" className="visually-hidden">
                {title}
              </span>
              <button
                type="button"
                className="btn-close ms-auto"
                aria-label="Fermer"
                onClick={onClose}
              />
            </div>
            <div className="modal-body pt-2 pb-4 px-4">
              {target.kind === "product" ? (
                <ProductDetailBody product={target.product} />
              ) : (
                <SolutionDetailBody solution={target.solution} products={target.products} />
              )}
            </div>
            <div className="modal-footer border-0 pt-0 px-4 pb-4 flex-wrap gap-2">
              <Link to={fullPageUrl} className="btn btn-outline-secondary" onClick={onClose}>
                Fiche complète →
              </Link>
              <button type="button" className="btn btn-outline-secondary ms-auto" onClick={onClose}>
                Fermer
              </button>
              <button
                type="button"
                className={`btn ${selected ? "btn-outline-primary" : "btn-primary"}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleSelect();
                }}
              >
                {selected ? "Retirer de la sélection" : "Ajouter à la sélection"}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop show" onClick={onClose} aria-hidden />
    </>
  );
}
